import { classNames } from "primereact/utils";
import { useMemo } from "react";
import { AppDialog } from "../../../common/components/AppDialog/AppDialog";
import { useLastWhileVisible } from "../../../common/components/AppDialog/hooks/useLastWhileVisible";
import { useStaysMountedToClose } from "../../../common/components/AppDialog/hooks/useStaysMountedToClose";
import { ItemsList } from "../../../common/components/itemsList/ItemsList";
import { ReservationItem } from "./components/ReservationItem";
import styles from "./MyReservations.module.scss";
import { ReservationDetails } from "./components/ReservationDetails";
import { MusicSuggestionsDialog } from "../musicSuggestions/MusicSuggestionsDialog";
import { useMyReservations } from "./hooks/useMyReservations";
import { useMyStudioReview } from "./hooks/useMyStudioReview";
import { LeaveReviewDialog } from "./components/LeaveReviewDialog";
import { useAppContext } from "../../../AppContext";
import { StudioType } from "../../../common/enums";
import { EventHistoryItem } from "../../../common/components/common/eventHistoryItem/EventHistoryItem";
import { IMyReservationsViewModel } from "./models/MyReservationsViewModel";
import { IEventHistoryItemViewModel } from "../../../common/models/eventHistoryItemViewModel";

type CombinedItem =
  | {
      kind: "reservation";
      id: string;
      date: string | Date;
      data: IMyReservationsViewModel;
    }
  | {
      kind: "gym";
      id: string;
      date: string | Date;
      data: IEventHistoryItemViewModel;
    };

export const MyReservations = () => {
  const { selectedStudio } = useAppContext();
  const isGymOnlyStudio =
    selectedStudio?.types?.length === 1 &&
    selectedStudio.types[0] === StudioType.Gym;
  const pageTitle = isGymOnlyStudio ? "Моите посещения" : "Моите резервации";
  const {
    userEvents,
    isLoading,
    isDesktopOrTablet,
    reservationToShowId,
    musicDialogEventId,
    hasReservationForMusicEvent,
    onEventReservationCancel,
    handleOpenMusicDialog,
    handleCloseMusicDialog,
    handleCloseReservationDialog,
    handleOpenReservationDialog,
  } = useMyReservations();
  const myReview = useMyStudioReview(selectedStudio?.id);
  // Mounted only around its use, but long enough to animate out with the event it showed.
  const isMusicDialogOpen = !!musicDialogEventId;
  const keepMusicDialog = useStaysMountedToClose(isMusicDialogOpen);
  const shownMusicEventId = useLastWhileVisible(isMusicDialogOpen, musicDialogEventId);

  const combinedItems = useMemo<CombinedItem[]>(() => {
    const reservations: CombinedItem[] = (userEvents?.allEvents ?? []).map(
      (r) => ({
        kind: "reservation",
        id: `r_${r.reservationId}`,
        date: r.eventDetails.date,
        data: r,
      }),
    );
    const gyms: CombinedItem[] = (userEvents?.gymVisits ?? []).map((g) => ({
      kind: "gym",
      id: `g_${g.id}`,
      date: g.date,
      data: g,
    }));
    return [...reservations, ...gyms].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [userEvents?.allEvents, userEvents?.gymVisits]);

  return (
    <>
      <ItemsList
        isLoading={isLoading}
        className={styles.root}
        title={pageTitle}
        noItemsText="Няма намерени резултати."
        items={combinedItems}
        itemTemplate={(item, index) => {
          if (!item) {
            return (
              <ReservationItem
                item={undefined}
                key={index}
                isHeadOfFamily={userEvents?.isHeadOfFamily ?? false}
                isLoading={isLoading}
                onClick={handleOpenReservationDialog}
              />
            );
          }
          if (item.kind === "gym") {
            return (
              <EventHistoryItem
                event={item.data}
                key={item.id}
                isLoading={false}
              />
            );
          }
          return (
            <ReservationItem
              item={item.data}
              key={item.id}
              isHeadOfFamily={userEvents?.isHeadOfFamily ?? false}
              isLoading={isLoading}
              onClick={handleOpenReservationDialog}
            />
          );
        }}
        enableSearch
        fullHeight
      />

      <AppDialog
        header={"Детайли за резервацията"}
        visible={!!reservationToShowId}
        onHide={handleCloseReservationDialog}
        style={{ width: isDesktopOrTablet ? "650px" : "90vw" }}
        headerClassName={classNames(
          styles.root_dialog,
          styles.root_dialog_header
        )}
        contentClassName={classNames(
          styles.root_dialog,
          styles.root_dialog_content
        )}
      >
        <ReservationDetails
          items={userEvents?.allEvents ?? []}
          selectedReservationId={reservationToShowId}
          canCancelReservations={userEvents?.canCancelReservations}
          selectedStudio={selectedStudio}
          allowLateCancellationsWithPayment={
            userEvents?.allowLateCancellationsWithPayment
          }
          onEventReservationCancel={onEventReservationCancel}
          onOpenMusicDialog={handleOpenMusicDialog}
          hasReview={myReview.hasReview}
          onLeaveReview={myReview.canReview ? myReview.openDialog : undefined}
        />
      </AppDialog>

      <LeaveReviewDialog
        visible={myReview.isDialogOpen}
        studioName={selectedStudio?.name}
        review={myReview.review}
        initialValues={myReview.initialValues}
        isSaving={myReview.isSaving}
        onSave={myReview.save}
        onHide={myReview.closeDialog}
      />

      {keepMusicDialog && shownMusicEventId && (
        <MusicSuggestionsDialog
          eventId={shownMusicEventId}
          isOpen={isMusicDialogOpen}
          onClose={handleCloseMusicDialog}
          showSearch={hasReservationForMusicEvent}
        />
      )}
    </>
  );
};
